import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Test, ListOptions, QueryFilter } from '@mariana/labos-client';
import type { PaginationInfo } from '../types';
import type { ResolvedEntityMap } from '../utils/resolveColumns';
import { fetchEntitiesByIds } from '../utils/resolveColumns';
import { limsClient } from '../api/limsClient';
import { ALL_ANALYTICAL_ASSAY_IDS, VIEW_REGISTRY } from '../config/viewsConfig';

interface CombinedRow {
  id: number;
  attributes: {
    sample_id: number;
    _nmr: Record<string, unknown> | null;
    _icp: Record<string, unknown> | null;
    _shimadzu: Record<string, unknown> | null;
    [key: string]: unknown;
  };
}

interface UseCombinedAnalyticalOptions {
  extraFilters?: QueryFilter[];
  search?: string;
  initialLimit?: number;
}

export interface UseCombinedAnalyticalResult {
  rows: CombinedRow[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  resolvedEntities: ResolvedEntityMap;
  handlePageChange: (offset: number) => void;
  handleLimitChange: (limit: number) => void;
  fetchAllRows: (onProgress: (fetched: number, total: number) => void) => Promise<CombinedRow[]>;
}

const NMR_ASSAY_IDS = new Set(VIEW_REGISTRY.nmr.filters[0].value as number[]);
const ICP_ASSAY_IDS = new Set(VIEW_REGISTRY.icp.filters[0].value as number[]);
const SHIMADZU_ASSAY_IDS = new Set(VIEW_REGISTRY.shimadzu.filters[0].value as number[]);

function getAssayCategory(assayId: number): 'nmr' | 'icp' | 'shimadzu' | null {
  if (NMR_ASSAY_IDS.has(assayId)) return 'nmr';
  if (ICP_ASSAY_IDS.has(assayId)) return 'icp';
  if (SHIMADZU_ASSAY_IDS.has(assayId)) return 'shimadzu';
  return null;
}

function pivotTests(tests: Test[]): CombinedRow[] {
  const bySample = new Map<number, {
    latest: { nmr: Test | null; icp: Test | null; shimadzu: Test | null };
  }>();

  for (const test of tests) {
    const attrs = test.attributes as Record<string, unknown> | undefined;
    const sampleId = attrs?.sample_id as number;
    if (!sampleId) continue;

    const assayId = attrs?.assay_id as number;
    const cat = getAssayCategory(assayId);
    if (!cat) continue;

    let entry = bySample.get(sampleId);
    if (!entry) {
      entry = { latest: { nmr: null, icp: null, shimadzu: null } };
      bySample.set(sampleId, entry);
    }

    const existing = entry.latest[cat];
    if (!existing || (test.id ?? 0) > (existing.id ?? 0)) {
      entry.latest[cat] = test;
    }
  }

  const rows: CombinedRow[] = [];
  bySample.forEach((entry, sampleId) => {
    const nmrAttrs = entry.latest.nmr?.attributes as Record<string, unknown> | undefined;
    const icpAttrs = entry.latest.icp?.attributes as Record<string, unknown> | undefined;
    const shimadzuAttrs = entry.latest.shimadzu?.attributes as Record<string, unknown> | undefined;

    rows.push({
      id: sampleId,
      attributes: {
        sample_id: sampleId,
        _nmr: nmrAttrs?.worksheet_data as Record<string, unknown> | null ?? null,
        _icp: icpAttrs?.worksheet_data as Record<string, unknown> | null ?? null,
        _shimadzu: shimadzuAttrs?.worksheet_data as Record<string, unknown> | null ?? null,
      },
    });
  });

  rows.sort((a, b) => b.id - a.id);
  return rows;
}

export function useCombinedAnalytical({
  extraFilters = [],
  search,
  initialLimit = 25,
}: UseCombinedAnalyticalOptions): UseCombinedAnalyticalResult {
  const [allRows, setAllRows] = useState<CombinedRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ limit: initialLimit, offset: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resolvedEntities, setResolvedEntities] = useState<ResolvedEntityMap>(new Map());

  const filtersKey = useMemo(() => JSON.stringify({ extraFilters, search, refreshKey }), [extraFilters, search, refreshKey]);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const baseFilter: QueryFilter = {
          attribute_name: 'assay_id',
          operator: 'IN',
          value: ALL_ANALYTICAL_ASSAY_IDS,
        };
        const mergedFilters = [baseFilter, ...extraFilters];

        const allTests: Test[] = [];
        let offset = 0;
        const batchSize = 1000;
        let total = Infinity;

        while (offset < total) {
          if (controller.signal.aborted) return;

          const options: ListOptions = {
            filters: mergedFilters,
            limit: batchSize,
            offset,
            sort_by: 'id',
            sort_order: 'desc',
            exclude_attributes: ['raw_worksheet_data'],
            ...(search && { search }),
          };

          const response = await limsClient.listEntitiesByType('test', options);
          if (controller.signal.aborted) return;

          allTests.push(...response.data as Test[]);
          total = response.pagination.count;
          offset += batchSize;
        }

        const pivoted = pivotTests(allTests);
        if (controller.signal.aborted) return;

        setAllRows(pivoted);
        setPagination(prev => ({ ...prev, offset: 0, count: pivoted.length }));
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load combined data');
        console.error('Failed to load combined analytical data:', err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const rows = useMemo(() => {
    return allRows.slice(pagination.offset, pagination.offset + pagination.limit);
  }, [allRows, pagination.offset, pagination.limit]);

  useEffect(() => {
    if (rows.length === 0) return;
    let cancelled = false;

    const sampleIds = Array.from(new Set(rows.map(r => r.attributes.sample_id)));
    fetchEntitiesByIds('sample', sampleIds).then(samples => {
      if (cancelled) return;
      const map: ResolvedEntityMap = new Map();
      const sampleMap = new Map<number, typeof samples[0]>();
      for (const s of samples) {
        if (s.id != null) sampleMap.set(s.id, s);
      }
      map.set('sample_id', sampleMap);
      setResolvedEntities(map);
    });

    return () => { cancelled = true; };
  }, [rows]);

  const handlePageChange = useCallback((newOffset: number) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 }));
  }, []);

  const fetchAllRows = useCallback(async (
    onProgress: (fetched: number, total: number) => void,
  ): Promise<CombinedRow[]> => {
    onProgress(allRows.length, allRows.length);
    return allRows;
  }, [allRows]);

  return {
    rows,
    pagination,
    loading,
    error,
    resolvedEntities,
    handlePageChange,
    handleLimitChange,
    fetchAllRows,
  };
}
